"use client";

import { useRef } from "react";

export interface StringPreviewDef {
  position: number;
  colorHex: string;
  gaugeValue?: string;
  note?: string;
}

interface Props {
  strings: StringPreviewDef[];
  height?: number;
  showLabels?: boolean;
  interactive?: boolean;
  className?: string;
}

export function StringPreview({
  strings,
  height = 100,
  showLabels = false,
  interactive = true,
  className,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  if (strings.length === 0) return null;

  const n = strings.length;
  const svgW = 400;
  const padY = 14;
  const padX = 14;
  const labelW = showLabels ? 22 : 0;
  const gaugeW = showLabels ? 36 : 0;
  const x1 = padX + labelW;
  const x2 = svgW - padX - gaugeW;
  const spacing = n > 1 ? (height - padY * 2) / (n - 1) : 0;

  function handleMouseEnter() {
    if (!interactive || !svgRef.current) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    svgRef.current.querySelectorAll<SVGGElement>(".str-group").forEach((g, i) => {
      // Higher strings (i=0, thinnest) vibrate faster
      const dur = 0.75 - i * 0.05;
      const delay = i * 28;
      g.style.animation = "none";
      // Force reflow so the animation restart is picked up
      void g.getBoundingClientRect();
      g.style.animation = `string-pluck ${dur}s ease-out ${delay}ms forwards`;
    });
  }

  return (
    <svg
      ref={svgRef}
      width="100%"
      height={height}
      viewBox={`0 0 ${svgW} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      className={className}
      onMouseEnter={handleMouseEnter}
      style={{ cursor: interactive ? "default" : undefined }}
      aria-hidden="true"
    >
      <defs>
        {strings.map((_, i) => (
          <filter key={i} id={`sg-${i}`} x="-10%" y="-400%" width="120%" height="900%">
            <feGaussianBlur stdDeviation="2.8" in="SourceGraphic" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        ))}
      </defs>

      <rect width={svgW} height={height} fill="#0D0D0D" rx="6" />

      {strings.map((str, i) => {
        const y = padY + i * spacing;
        const sw = 0.9 + (i / Math.max(n - 1, 1)) * 2.4;
        const hex = str.colorHex ?? "#E84040";

        return (
          <g key={str.position} className="str-group">
            {/* Glow */}
            <line
              x1={x1} y1={y} x2={x2} y2={y}
              stroke={hex}
              strokeWidth={sw + 2.5}
              opacity={0.3}
              filter={`url(#sg-${i})`}
            />
            {/* String body */}
            <line
              x1={x1} y1={y} x2={x2} y2={y}
              stroke={hex}
              strokeWidth={sw}
              strokeLinecap="round"
            />
            {/* Specular highlight */}
            <line
              x1={x1} y1={y - sw * 0.35} x2={x2} y2={y - sw * 0.35}
              stroke="rgba(255,255,255,0.22)"
              strokeWidth={sw * 0.35}
              strokeLinecap="round"
            />
            {showLabels && str.note ? (
              <text
                x={padX + labelW - 4}
                y={y + 3.5}
                fontSize="10"
                fill="#8A8A8A"
                textAnchor="end"
                fontFamily="monospace"
              >
                {str.note}
              </text>
            ) : null}
            {showLabels && str.gaugeValue ? (
              <text
                x={svgW - padX}
                y={y + 3.5}
                fontSize="9"
                fill="#3A3A3A"
                textAnchor="end"
                fontFamily="monospace"
              >
                {str.gaugeValue}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}
